import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspaceStore, useEditorStore } from '@/store';
import * as fileService from '@/services/file.service';
import IDELayout from '@/components/layout/IDELayout';
import { Code2, FolderOpen } from 'lucide-react';

export default function EditorPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { workspaces, activeWorkspace, selectWorkspace, fetchWorkspaces, startWorkspace } =
    useWorkspaceStore();
  const { setFileTree } = useEditorStore();

  // On mount, set the active workspace from URL param
  useEffect(() => {
    if (!workspaceId) return;

    const ws = workspaces.find((w) => w.id === workspaceId);
    if (ws) {
      selectWorkspace(ws);
    } else {
      // Fetch workspaces if not loaded
      fetchWorkspaces().then(() => {
        const found = useWorkspaceStore.getState().workspaces.find((w) => w.id === workspaceId);
        if (found) {
          selectWorkspace(found);
        }
      });
    }
  }, [workspaceId, workspaces, selectWorkspace, fetchWorkspaces]);

  // Fetch file tree when workspace is active and running
  useEffect(() => {
    if (!activeWorkspace || activeWorkspace.status !== 'running') return;

    fileService.getFileTree(activeWorkspace.id).then((tree) => {
      if (tree.length > 0) {
        setFileTree({
          path: '/',
          name: activeWorkspace.name,
          type: 'directory',
          children: tree,
        });
      }
    });
  }, [activeWorkspace, setFileTree]);

  // No workspace selected — show overlay
  if (!activeWorkspace) {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-[#1e1e1e]">
        <div className="text-center space-y-6">
          <Code2 className="w-16 h-16 text-[#858585] mx-auto" />
          <h1 className="text-2xl font-semibold text-white">
            No Workspace Selected
          </h1>
          <p className="text-[#858585] max-w-md">
            Open a workspace to start coding, or create a new one from the
            projects page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0e639c] hover:bg-[#1177bb] text-white rounded text-sm transition-colors"
          >
            <FolderOpen className="w-4 h-4" />
            Open Projects
          </button>
        </div>
      </div>
    );
  }

  // Workspace exists but not running — show start prompt
  if (activeWorkspace.status !== 'running') {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-[#1e1e1e]">
        <div className="text-center space-y-6">
          <Code2 className="w-16 h-16 text-yellow-500 mx-auto" />
          <h1 className="text-2xl font-semibold text-white">
            {activeWorkspace.name}
          </h1>
          <p className="text-[#858585]">
            Workspace is <span className="text-yellow-400">{activeWorkspace.status}</span>.
            Start it to begin editing.
          </p>
          <button
            onClick={() => startWorkspace(activeWorkspace.id)}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
          >
            Start Workspace
          </button>
        </div>
      </div>
    );
  }

  return <IDELayout />;
}
